import { helpHref, type HelpOpen } from "../help/catalog";

export type PlaybookId = "org-admin" | "salesperson" | "sales-manager" | "app-admin";

export interface PlaybookNeed {
  capability?: string;
  action?: string;
  orgAdmin?: boolean;
}

export interface PlaybookStep {
  id: string;
  title: string;
  body: string;
  open?: HelpOpen;
  target?: string;
  /** Extra nodes included in the spotlight hole so a dimmed control stays reachable (e.g. legal name + Create). */
  spot?: string[];
  advanceOnClick?: boolean;
  /** Next stays put until the target input has a non-empty value (e.g. legal name before Create). */
  requireFilled?: boolean;
  /** A required setup tour can skip this step. The click is a real save, and Next stays grey. */
  skippable?: boolean;
  need?: PlaybookNeed;
}

export interface Playbook {
  id: PlaybookId;
  title: string;
  blurb: string;
  audience: string;
  steps: PlaybookStep[];
}

export interface PlaybookAccess {
  isOrgAdmin: boolean;
  can: (capability: string, action?: string) => boolean;
}

export const PLAYBOOKS: Playbook[] = [
  {
    id: "org-admin",
    title: "Set up your workspace",
    blurb: "Find organisation settings, set currency, connect company email, add people, and pick roles.",
    audience: "Organization admin",
    steps: [
      {
        id: "welcome",
        title: "Click your avatar",
        body: "This tour shows where organisation settings live, including currency, users, and roles. Start here: click your avatar in the top right of the screen. That opens your account menu.",
        open: { kind: "dashboard" },
        target: "account-menu",
        advanceOnClick: true,
        need: { orgAdmin: true },
      },
      {
        id: "open-org-settings",
        title: "Click Organisation settings",
        body: "In the menu that opened, click Organisation settings. That is the organisation settings page — not User settings.",
        target: "org-settings",
        advanceOnClick: true,
        need: { orgAdmin: true },
      },
      {
        id: "org-settings-page",
        title: "This is Organisation settings",
        body: "Everything that shapes your workspace is managed from here. Users, User Roles, Company Info, Territories & Locale, and App Settings (company email) are in the tab row under the heading, with Plan & Billing and the rest.",
        open: { kind: "settings" },
        target: "settings-heading",
        need: { orgAdmin: true },
      },
      {
        id: "theme",
        title: "Choose light or dark",
        body: "The Light / Dark button in the top bar changes the look of the whole workspace. Click it to switch and see the difference, and click again to switch back. Your choice is saved to your account and you can change it at any time. Select Next when you are happy.",
        target: "theme-toggle",
        need: { orgAdmin: true },
      },
      {
        id: "open-app",
        title: "Click Territories & Locale",
        body: "In the tab row, click Territories & Locale. The Territory dropdown lists Global, and New territory adds an office. Country, timezone, currency, tax, and quote approval for that office live on the same tab.",
        target: "org-locale-tab",
        advanceOnClick: true,
        need: { orgAdmin: true },
      },
      {
        id: "timezone",
        title: "Country and timezone",
        body: "Choose the Country your organisation is based in (LeadGen searches postcodes in it), then the Timezone. Dates, times, reminders, and reports follow the timezone. People can still localise their own screens under User settings.",
        open: { kind: "settings", path: "?tab=locale" },
        target: "org-timezone",
        need: { orgAdmin: true },
      },
      {
        id: "currency",
        title: "Currency",
        body: "This Currency list is the organization default for new quotes, orders, and invoices. Date format, time format, and number format below it only change how dates and amounts are shown. They do not change numbers already stored. Pick yours, then save in the next step.",
        open: { kind: "settings", path: "?tab=locale" },
        target: "org-currency",
        need: { orgAdmin: true },
      },
      {
        id: "fiscal-year",
        title: "Fiscal year",
        body: "Choose the month your financial year starts. Forecast and the pipeline charts group quarters from it. Leave January to keep quarters on the calendar year. The quote-approval option is the next control down.",
        open: { kind: "settings", path: "?tab=locale" },
        target: "org-fiscal-year",
        need: { orgAdmin: true },
      },
      {
        id: "quote-approval",
        title: "Quote approval",
        body: "This checkbox sits under Fiscal year starts in and above Tax rate. Tick it when a sales manager must approve a quote before it can be sent. It is stored with the locale settings in the next save.",
        open: { kind: "settings", path: "?tab=locale" },
        target: "org-quote-approval",
        need: { orgAdmin: true },
      },
      {
        id: "tax",
        title: "Tax rate",
        body: "Enter your standard Tax rate (%). It is applied to quotes, orders, and invoices. Leave it blank if you do not charge tax. It is saved together with the settings above in the next step.",
        open: { kind: "settings", path: "?tab=locale" },
        target: "org-tax-rate",
        need: { orgAdmin: true },
      },
      {
        id: "save-locale",
        title: "Click Save locale settings",
        body: "Under the Territories & Locale form, click Save locale settings. That stores your country, timezone, currency, date and number formats, fiscal year, tax rate, and quote approval for everyone in the organisation.",
        open: { kind: "settings", path: "?tab=locale" },
        target: "org-save-locale",
        advanceOnClick: true,
        skippable: true,
        need: { orgAdmin: true },
      },
      {
        id: "company-details",
        title: "Click Save company details",
        body: "Company Info is the letterhead for quotes, orders, and invoices: logo, address, tax number, and your company website (LeadGen needs the website). Add what you have now — nothing here is compulsory and you can complete it later. Company website is the last field. Under the Company website field, click Save company details to continue.",
        open: { kind: "settings", path: "?tab=company" },
        target: "org-save-company",
        spot: ["org-company-website", "org-save-company"],
        advanceOnClick: true,
        skippable: true,
        need: { orgAdmin: true },
      },
      {
        id: "open-email",
        title: "Click App Settings",
        body: "In the tab row, click App Settings. Company email — the shared mailbox Saleslox reads — is configured on that tab, not on the Mailbox screen.",
        target: "org-app-tab",
        advanceOnClick: true,
        need: { orgAdmin: true },
      },
      {
        id: "email",
        title: "Company email",
        body: "When Enable the shared mailbox is on, choose a password login or Microsoft 365. A password login needs the IMAP host, username, and password. Microsoft 365 needs the shared mailbox address, then Sign in with Microsoft. Each check reads the last 60 days, not the whole mailbox. Save mailbox settings, Test connection, and Check now sit under the form. Leave the password blank to keep one that is already saved. Select Next when you have seen where this lives.",
        open: { kind: "settings", path: "?tab=app" },
        target: "org-mailbox",
        need: { orgAdmin: true },
      },
      {
        id: "add-person",
        title: "This form creates the account",
        body: "You are back on Users. Fill email and name, tick a role, then click Add user. After save, copy or email the password setup link — we never send a password. You can finish that after the tour.",
        open: { kind: "settings" },
        target: "add-person",
        need: { orgAdmin: true },
      },
      {
        id: "roles",
        title: "Click User Roles",
        body: "In the tab row, click User Roles, next to Users, to choose which tabs each role can open.",
        open: { kind: "settings" },
        target: "people-roles-tab",
        advanceOnClick: true,
        need: { orgAdmin: true },
      },
      {
        id: "role-tabs",
        title: "Roles bundle tabs",
        body: "Each role is a bundle of tabs. Tick what a Sales person or Sales manager should see. Tabs above your plan stay visible but stay locked until you upgrade.",
        open: { kind: "settings", path: "?tab=roles" },
        target: "people-roles",
        need: { orgAdmin: true },
      },
      {
        id: "help",
        title: "Click Help",
        body: "Click Help in the top bar, to the left of your avatar. Organization admin → Users and roles walks the same path. Replay this tour later from your avatar → Guided tours.",
        target: "nav-help",
        advanceOnClick: true,
        need: { orgAdmin: true },
      },
    ],
  },
  {
    id: "salesperson",
    title: "Win your first account",
    blurb: "Create a company, add a contact, and capture a lead.",
    audience: "Sales person",
    steps: [
      {
        id: "welcome",
        title: "Click CRM",
        body: "Work starts with a company (the account), then a person, then a lead. Start here: click CRM in the top bar, to the right of My Stats.",
        open: { kind: "dashboard" },
        target: "app-crm",
        advanceOnClick: true,
        need: { capability: "crm.companies" },
      },
      {
        id: "open-companies",
        title: "Click Companies",
        body: "In the CRM tab row just under the top bar, click Companies.",
        target: "crm-tab-crm.companies",
        advanceOnClick: true,
        need: { capability: "crm.companies" },
      },
      {
        id: "companies",
        title: "Click New company",
        body: "On the right of this list, click New company.",
        open: { kind: "crm", path: "/companies" },
        target: "new-company",
        advanceOnClick: true,
        need: { capability: "crm.companies", action: "create" },
      },
      {
        id: "legal-name",
        title: "Legal name",
        body: "Type the customer’s legal name in the highlighted field. Possible duplicates appear as you type. Next unlocks once the field has a name.",
        open: { kind: "crm", path: "/companies" },
        target: "company-legal-name",
        requireFilled: true,
        need: { capability: "crm.companies", action: "create" },
      },
      {
        id: "create",
        title: "Click Create company",
        body: "Under the legal name field, click Create company. That opens the company record; the following step is Contacts on that record, not on this list. If the button is dimmed, type a legal name first. Next stays grey until you click Create company.",
        open: { kind: "crm", path: "/companies" },
        target: "create-company",
        spot: ["company-legal-name", "create-company"],
        advanceOnClick: true,
        need: { capability: "crm.companies", action: "create" },
      },
      {
        id: "contacts",
        title: "Click Contacts",
        body: "On this company record, click Contacts in the tab row under the company name.",
        target: "record-tab-contacts",
        advanceOnClick: true,
        need: { capability: "crm.companies" },
      },
      {
        id: "add-contact",
        title: "Add a contact",
        body: "Click New contact. Fill first name and click Create contact. Tick Marketing enabled only when they may receive campaigns. You can finish that after the tour.",
        target: "add-contact",
        need: { capability: "crm.companies" },
      },
      {
        id: "new-lead",
        title: "Click New lead",
        body: "On the right of this record, click New lead. Set source and Temperature (estimate), then Add lead. Convert later from the lead, or by dragging its card onto Converted on the Leads board.",
        target: "new-lead",
        advanceOnClick: true,
        need: { capability: "crm.leads", action: "create" },
      },
      {
        id: "record-path",
        title: "Contacts live on the record",
        body: "When a company is open, Contacts is a tab under the name, not on the companies list. New lead is on the right of that record. You can finish creating after the tour. Replay from your avatar → Guided tours.",
        need: { capability: "crm.companies" },
      },
    ],
  },
  {
    id: "sales-manager",
    title: "Coach the pipeline",
    blurb: "Opportunities, forecast, and the sales-cycle map.",
    audience: "Sales manager",
    steps: [
      {
        id: "welcome",
        title: "Click CRM",
        body: "Managers coach from Opportunities. Professional and Enterprise also unlock Forecast and Sales cycle — this tour only highlights tabs your plan and role can open. Start here: click CRM in the top bar, to the right of My Stats.",
        open: { kind: "dashboard" },
        target: "app-crm",
        advanceOnClick: true,
      },
      {
        id: "opportunities",
        title: "Click Opportunities",
        body: "In the CRM tab row just under the top bar, click Opportunities. The board that opens is the working view. Drag a card to change stage. Convert a lead from Leads to land a deal in Lead In.",
        target: "crm-tab-crm.opportunities",
        advanceOnClick: true,
        need: { capability: "crm.opportunities" },
      },
      {
        id: "forecast",
        title: "Click Forecast & KPI",
        body: "In the same CRM tab row, click Forecast & KPI. Weighted pipeline versus yearly targets. Type a yearly number and Save if you can configure; a sales plan can set each month or quarter instead. This tab needs Professional or Enterprise.",
        target: "crm-tab-crm.forecast",
        advanceOnClick: true,
        need: { capability: "crm.forecast" },
      },
      {
        id: "cycle",
        title: "Click Sales cycle",
        body: "In the same CRM tab row, click Sales cycle. Start a cycle from a company or this tab. The left column is the timed flow — start a stage or let the dates auto-start it. Click a stage on the map, move tasks To do → Doing → Done, and leave a comment — it lands on the company timeline. Delete on the list or the run removes that customer's cycle. This tab needs Professional or Enterprise.",
        target: "crm-tab-crm.sales_cycle",
        advanceOnClick: true,
        need: { capability: "crm.sales_cycle" },
      },
    ],
  },
  {
    id: "app-admin",
    title: "Shape the CRM",
    blurb: "Layouts, company workflow, and document templates.",
    audience: "App admin",
    steps: [
      {
        id: "welcome",
        title: "Click CRM",
        body: "App admin designs how records look. It does not replace Users or User roles — ask an org admin for seats and tabs. Start here: click CRM in the top bar, to the right of My Stats.",
        open: { kind: "dashboard" },
        target: "app-crm",
        advanceOnClick: true,
        need: { capability: "crm.companies" },
      },
      {
        id: "designer",
        title: "Click App Designer",
        body: "In the CRM tab row just under the top bar, click App Designer. Pick a screen, tick fields onto a section, then Save layout. Restore default puts the shipped layout back.",
        target: "crm-tab-crm.app.design",
        advanceOnClick: true,
        need: { capability: "crm.app.design" },
      },
      {
        id: "open-companies",
        title: "Click Companies",
        body: "In the same CRM tab row, click Companies.",
        target: "crm-tab-crm.companies",
        advanceOnClick: true,
        need: { capability: "crm.companies" },
      },
      {
        id: "company-settings",
        title: "Click Company settings",
        body: "On the right of the companies list, click Company settings. Interval reminders and workflow rules live there.",
        open: { kind: "crm", path: "/companies" },
        target: "company-settings",
        advanceOnClick: true,
        need: { capability: "crm.companies", action: "configure" },
      },
      {
        id: "documents",
        title: "Click Template Designer",
        body: "In the CRM tab row, click Template Designer. Quote and order templates live here. Invoice templates need Enterprise. Save before anyone sends a live quote.",
        target: "crm-tab-crm.documents",
        advanceOnClick: true,
        need: { capability: "crm.documents" },
      },
    ],
  },
];

export function playbookById(id: PlaybookId): Playbook | undefined {
  return PLAYBOOKS.find((item) => item.id === id);
}

export function playbookHref(step: PlaybookStep, orgSlug: string | null | undefined): string | null {
  return helpHref(step.open, orgSlug);
}

export function stepAllowed(step: PlaybookStep, input: PlaybookAccess): boolean {
  if (step.need?.orgAdmin && !input.isOrgAdmin) return false;
  if (step.need?.capability && !input.can(step.need.capability, step.need.action)) return false;
  return true;
}

/**
 * Who sees which tour when they are not an organization admin (an org admin sees every tour):
 * any one of the needs is enough. Order is the order the tours are offered in. Data, not code,
 * so Loxley's export (help/helpExport.ts) applies the same rule server-side.
 * Coach the pipeline needs Forecast or Sales cycle. Opening Opportunities is not enough, so a
 * salesperson does not see that tour when those two steps have been left out.
 */
export const PLAYBOOK_ACCESS: { id: PlaybookId; anyOf: PlaybookNeed[] }[] = [
  {
    id: "app-admin",
    anyOf: [{ capability: "crm.app.design" }, { capability: "crm.companies", action: "configure" }, { capability: "crm.documents" }],
  },
  {
    id: "sales-manager",
    anyOf: [{ capability: "crm.forecast" }, { capability: "crm.sales_cycle" }],
  },
  { id: "salesperson", anyOf: [{ capability: "crm.companies" }] },
];

export function availablePlaybookIds(input: PlaybookAccess): PlaybookId[] {
  if (input.isOrgAdmin) return PLAYBOOKS.map((item) => item.id);
  return PLAYBOOK_ACCESS
    .filter((entry) => entry.anyOf.some((need) => need.capability && input.can(need.capability, need.action)))
    .map((entry) => entry.id);
}

export function visiblePlaybooks(input: PlaybookAccess): Playbook[] {
  return PLAYBOOKS
    .filter((item) => availablePlaybookIds(input).includes(item.id))
    .map((item) => ({ ...item, steps: item.steps.filter((step) => stepAllowed(step, input)) }))
    .filter((item) => item.steps.length >= 2);
}

/**
 * Tours an organization admin must finish while on a trial: just the setup tour
 * (locale, users, roles). The first-account tour is offered afterwards, not forced.
 */
export const ONBOARDING_SEQUENCE: PlaybookId[] = ["org-admin"];

export function nextOnboardingId(
  store: Partial<Record<PlaybookId, { status: string }>>,
  available: PlaybookId[],
): PlaybookId | null {
  return ONBOARDING_SEQUENCE.find((id) => available.includes(id) && store[id]?.status !== "completed") ?? null;
}

export function primaryPlaybookId(ids: PlaybookId[]): PlaybookId | null {
  const rank: PlaybookId[] = ["org-admin", "sales-manager", "salesperson", "app-admin"];
  return rank.find((id) => ids.includes(id)) ?? ids[0] ?? null;
}

export function isAccountMenuTarget(target: string | undefined): boolean {
  return target === "org-settings" || target === "replay-tours";
}
